import * as styles from "./PlannedExpensesDialog.css";

import { clsx } from "clsx";
import { useAtomValue, useSetAtom } from "jotai";
import { type JSX, type RefObject, useMemo, useRef } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Icon } from "@frontend/components/atoms/Icon";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
} from "@frontend/components/organisms/DataTable";
import { useArrayField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { formatAmount, parseAmount } from "@frontend/utils/amount";
import { Trans, useLingui } from "@lingui/react/macro";

import type { PlannedExpenseValues } from "../schemas/project";
import {
  addPlannedExpenseAtom,
  editedExpenseAtom,
  optionsAtom,
  projectForm,
} from "../stores/editor";
import { plannedExpenseTotal, plannedExpensesTotal } from "../utils/expenses";
import { PlannedExpenseDialog } from "./PlannedExpenseDialog";

const fields = projectForm.fields.intervention.fields;

const DIALOG_WIDTH = 900;

type ExpenseRow = {
  index: number;
  path: string;
  typeLabel: string | null;
  quantity: number | null;
  unitLabel: string | null;
  unitPrice: number | null;
  recurrenceLabel: string | null;
  total: number | null;
  incomplete: boolean;
};

const helper = createDataTableColumnHelper<ExpenseRow>();

export type PlannedExpensesDialogProps = {
  originRef?: RefObject<DOMRect | null>;
  onClose: () => void;
};

/**
 * The expenses of the intervention, one row each, as the work posts of a
 * subscription are listed. They are values of the project form: the popup
 * edits them in place, and the project's own Save carries them.
 */
export function PlannedExpensesDialog({
  originRef,
  onClose,
}: PlannedExpensesDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const options = useAtomValue(optionsAtom);
  const errors = useAtomValue(projectForm.state.errors);

  const expenses = useArrayField(fields.plannedExpenses);
  const values = useAtomValue(fields.plannedExpenses.valueAtom);

  const edited = useAtomValue(editedExpenseAtom);
  const setEdited = useSetAtom(editedExpenseAtom);
  const addExpense = useSetAtom(addPlannedExpenseAtom);

  // The row the editing popup grows from.
  const rowRef = useRef<DOMRect | null>(null);

  const rows = useMemo<ExpenseRow[]>(
    () =>
      values.map((expense: PlannedExpenseValues, index) => {
        const path = expenses.items[index]?.path ?? String(index);
        const type = options.expenseTypes.find(
          ({ id }) => id === expense.typeId,
        );

        return {
          index,
          path,
          typeLabel: type?.label ?? null,
          quantity: expense.quantity ?? null,
          unitLabel: type?.unitLabel ?? null,
          unitPrice: parseAmount(expense.unitPrice),
          recurrenceLabel:
            options.expenseRecurrences.find(
              ({ id }) => id === expense.recurrenceId,
            )?.label ?? null,
          total: plannedExpenseTotal(expense),
          incomplete: Object.keys(errors).some((key) =>
            key.startsWith(`${path}.`),
          ),
        };
      }),
    [values, expenses.items, options, errors],
  );

  const total = plannedExpensesTotal(values);

  const openRow = (index: number, origin: HTMLElement) => {
    rowRef.current = origin.getBoundingClientRect();
    setEdited({ index, created: false });
  };

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("typeLabel", {
          header: t`Type`,
          enableSorting: false,
          meta: { minWidth: "150px", truncate: true },
          cell: ({ getValue, row }) => (
            <>
              {getValue() ?? (
                <span className={styles.untyped}>
                  <Trans>Expense to fill in</Trans>
                </span>
              )}

              {row.original.incomplete ? (
                <>
                  {" "}
                  <Badge variant="warning" size="sm">
                    <Trans>Incomplete</Trans>
                  </Badge>
                </>
              ) : null}
            </>
          ),
        }),
        helper.accessor("quantity", {
          header: t`Quantity`,
          enableSorting: false,
          meta: { width: "90px" },
          cell: ({ getValue, row }) =>
            null === getValue() ? (
              <span className={styles.numeric}>&ndash;</span>
            ) : (
              <span className={styles.numeric}>
                {`${getValue()} ${row.original.unitLabel ?? ""}`.trim()}
              </span>
            ),
        }),
        helper.accessor("unitPrice", {
          header: t`Unit price`,
          enableSorting: false,
          meta: { width: "110px" },
          cell: ({ getValue }) => {
            const price = getValue();

            return (
              <span className={styles.numeric}>
                {null === price
                  ? "\u2013"
                  : `${formatAmount(price, locale)} ${t`Frs`}`}
              </span>
            );
          },
        }),
        helper.accessor("recurrenceLabel", {
          header: t`Recurrence`,
          enableSorting: false,
          meta: { minWidth: "110px", truncate: true },
          cell: ({ getValue }) =>
            getValue() ?? <span className={styles.muted}>&ndash;</span>,
        }),
        helper.accessor("total", {
          header: t`Total`,
          enableSorting: false,
          meta: { width: "110px" },
          cell: ({ getValue }) => {
            const amount = getValue();

            return (
              <span className={clsx(styles.numeric, styles.strong)}>
                {null === amount
                  ? "\u2013"
                  : `${formatAmount(amount, locale)} ${t`Frs`}`}
              </span>
            );
          },
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          meta: { width: "80px" },
          cell: ({ row }) => (
            <span className={styles.rowActions}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Edit this expense`}
                      onClick={(event) => {
                        event.stopPropagation();
                        openRow(row.original.index, event.currentTarget);
                      }}
                    >
                      <Icon name="Pencil" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Edit this expense`}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Remove this expense`}
                      onClick={(event) => {
                        event.stopPropagation();
                        const itemAtom = expenses.itemAtoms[row.original.index];

                        if (itemAtom) {
                          expenses.remove(itemAtom);
                        }
                      }}
                    >
                      <Icon name="Trash2" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Remove this expense`}
                </TooltipContent>
              </Tooltip>
            </span>
          ),
        }),
      ]),
    // The actions read the array field, which changes with every row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, locale, expenses.itemAtoms],
  );

  const editedItem =
    null === edited ? null : (expenses.items[edited.index] ?? null);

  const editedAtom = null === edited ? null : expenses.itemAtoms[edited.index];

  return (
    <TooltipProvider>
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next) {
            onClose();
          }
        }}
        originRef={originRef}
      >
        <DialogContent size={DIALOG_WIDTH}>
          <DialogHeader>
            <DialogTitle>{t`Planned expenses`}</DialogTitle>

            <DialogClose />
          </DialogHeader>

          <div className={styles.body}>
            <Panel overflow="visible">
              <Toolbar wrap={false}>
                <ToolbarSection>
                  <Button
                    variant="link"
                    size="sm"
                    leftSection={<Icon name="Plus" size="sm" />}
                    onClick={() => addExpense()}
                  >
                    {t`Add a planned expense`}
                  </Button>
                </ToolbarSection>
              </Toolbar>

              <DataTable
                columns={columns}
                data={rows}
                getRowId={(row) => row.path}
                emptyMessage={<Trans>No expense planned.</Trans>}
                size="sm"
                variant="ghost"
                horizontalSpacing="sm"
                verticalSpacing="sm"
                bordered={false}
                onRowClick={(row) => {
                  setEdited({ index: row.index, created: false });
                }}
              />

              <div className={styles.footer}>
                <span className={styles.muted}>
                  <Trans>Total</Trans>
                </span>

                <span className={styles.strong}>
                  {null === total
                    ? "–"
                    : `${formatAmount(total, locale)} ${t`Frs`}`}
                </span>
              </div>
            </Panel>
          </div>

          <DialogFooter>
            <Button variant="primary" onClick={onClose}>
              <Trans>Close</Trans>
            </Button>
          </DialogFooter>

          {editedItem && editedAtom && null !== edited ? (
            <PlannedExpenseDialog
              key={edited.index}
              path={editedItem.path}
              fields={editedItem.fields}
              itemAtom={editedAtom}
              created={edited.created}
              onRemove={() => {
                expenses.remove(editedAtom);
                setEdited(null);
              }}
              originRef={rowRef}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
